/**
 * Erreurs métier — format uniforme backend → frontend (ARCHITECTURE §5).
 *
 * Le corps d'une réponse en erreur est TOUJOURS :
 *
 * ```json
 * { "erreur": { "code": "SIEGE_OCCUPE",
 *               "message": "Le siège F12 est déjà occupé pour cette représentation.",
 *               "details": { "siege": "F12", "representationId": "…" } } }
 * ```
 *
 * Règle d'IHM (ARCHITECTURE §9.4) : le frontend affiche `message` tel quel sur
 * les 409 — jamais « une erreur est survenue ». Les messages sont donc écrits
 * ici pour être lus par le personnel du théâtre, pas par un développeur.
 */
/* -------------------------------------------------------------------------- */
/* Codes                                                                      */
/* -------------------------------------------------------------------------- */
export const CODE_ERREUR = [
    /** I-01 — un siège, un billet valide, une représentation. LE cœur du système. */
    'SIEGE_OCCUPE',
    /** TAR-04 — tarif absent de la grille : la vente est bloquée, jamais 0.– en silence. */
    'TARIF_MANQUANT',
    /**
     * ★ TAR-04 bis — la case existe et elle est FERMÉE : le théâtre a décidé de
     * ne pas vendre cette catégorie ici. Séparé de `TARIF_MANQUANT` parce qu'un
     * guichetier qui lit « manquant » remplit la case, et défait ainsi la
     * décision qu'il devait respecter.
     */
    'CATEGORIE_NON_VENDUE',
    /** I-03 — carnet : plus assez de crédits. */
    'SOLDE_INSUFFISANT',
    /** I-04 — formule « au choix » : ce spectacle est déjà consommé. */
    'SPECTACLE_DEJA_CONSOMME',
    /**
     * ★ T-32 — I-04, cas distinct : le QUOTA de spectacles de la formule est
     * atteint (pas de spectacle-doublon). Séparé de `SPECTACLE_DEJA_CONSOMME` :
     * les deux causes exigent des gestes différents — attendre une autre
     * représentation du MÊME spectacle n'aide pas ici, il faut soit renoncer,
     * soit une dérogation d'administrateur (DEB-10, T-18).
     */
    'QUOTA_ABONNEMENT_ATTEINT',
    /** I-05 — le siège n'appartient pas à la zone de la formule. */
    'ZONE_INCOMPATIBLE',
    /** I-06 — siège marqué non vendable (régie, PMR, défectueux — CFG-03). */
    'SIEGE_HORS_VENTE',
    /** CAM-04 — siège gelé par une proposition de reconduction en attente. */
    'SIEGE_GELE_CAMPAGNE',
    /** SOU-01 — siège promis par une souscription série encore vivante. */
    'SIEGE_RESERVE_SOUSCRIPTION',
    /**
     * ★ I-09 — le siège n'appartient pas à la salle où se joue la représentation.
     *
     * Tant qu'un théâtre n'avait qu'une salle, la question ne se posait pas.
     * Depuis que `representation` porte sa salle (migration 0012), rien
     * n'empêchait plus, côté requête, de vendre le F12 **de la petite salle**
     * pour une soirée jouée dans la grande : deux fauteuils portent le même
     * libellé, l'un est libre, l'autre est occupé, et le porteur du billet se
     * présente devant un siège qui n'existe pas là où il croit aller.
     */
    'SIEGE_HORS_SALLE',
    /** CAT-04 — la représentation visée est annulée : elle ne porte plus rien. */
    'REPRESENTATION_ANNULEE',
    /** L'action est impossible dans la phase actuelle de la saison. */
    'PHASE_INVALIDE',
    /** CFG-04 — la structure du plan est figée : des billets existent. */
    'PLAN_VERROUILLE',
    /** VEN-02 — vente à l'unité bloquée en phase interne (dérogation admin possible). */
    'VENTE_PHASE_INTERNE',
    /**
     * ★ T-18 (DEB-10) — un tarif `reserveGuichet` (étudiant, AVS…) demandé par
     * téléphone, sans dérogation d'administrateur. Distingué de `VALIDATION`
     * pour la même raison que `QUOTA_ABONNEMENT_ATTEINT` : le front doit pouvoir
     * offrir la dérogation sans deviner un code générique au message.
     */
    'CATEGORIE_RESERVEE_GUICHET',
    /** Authentification absente/expirée (401) ou rôle insuffisant (403). */
    'NON_AUTORISE',
    /** Ressource inexistante (404). */
    'INTROUVABLE',
    /** Données invalides : règle de saisie (400) ou schéma non respecté (422). */
    'VALIDATION',
    /**
     * ★ T-32 — 429, limitation de débit (A-02 : login, demande de lien magique,
     * inscription). Vivait sous `VALIDATION` depuis l'origine — un 429 n'est pas
     * une donnée invalide, c'est un débit dépassé ; le distinguer permet au
     * front d'afficher un décompte plutôt qu'un message de saisie.
     */
    'TROP_DE_DEMANDES',
    /**
     * ★★ LOT A — 503, le stockage des fichiers ne répond pas.
     *
     * Code DISTINCT, et c'est tout l'objet du choix. Sans lui, une panne de
     * stockage S3 remontait en `ERREUR_INTERNE` : un 500 anonyme (« Une erreur est
     * survenue, référence a3f2… ») sur un écran d'administration, pour un logo
     * qui ne se charge pas. Or ce n'est ni une erreur de programmation, ni une
     * panne du produit — c'est un service annexe momentanément absent, pendant
     * que la vente, le scan et les connexions continuent de fonctionner
     * normalement. Le distinguer permet de le DIRE à l'utilisateur, et permet à
     * la supervision de ne pas confondre un incident de stockage avec un défaut
     * applicatif.
     */
    'STOCKAGE_INDISPONIBLE',
    /* ---------------------------------------------------------------------- */
    /* Boutique publique et rétention (SPEC-BOUTIQUE §2.4, §3.1)               */
    /* ---------------------------------------------------------------------- */
    /*
     * ★ Ces quatre codes existent parce que le visiteur du site n'est PAS le
     * guichetier. Un guichetier à qui l'on répond « INTROUVABLE » comprend et
     * recommence ; un visiteur qui a saisi ses coordonnées pendant que son panier
     * expirait mérite qu'on lui dise ce qui s'est passé et ce qu'il peut faire.
     * Chacun de ces codes correspond donc à un ÉCRAN de la boutique, pas
     * seulement à une branche de code.
     */
    /** REZ-01/04 — le compte à rebours est écoulé, les places sont reparties. */
    'PANIER_EXPIRE',
    /** REZ-05 — nombre maximal de places par commande atteint. */
    'PANIER_PLEIN',
    /** REZ-03 — la prolongation a déjà été utilisée : elle n'est offerte qu'une fois. */
    'PROLONGATION_IMPOSSIBLE',
    /**
     * La boutique n'est pas ouverte pour cette saison : phase ≠ `externe`
     * (préparation, réabonnement en cours, saison close). Distinct de
     * `VENTE_PHASE_INTERNE`, qui annonce au guichet qu'un administrateur peut
     * déroger — au public, il n'y a rien à déroger.
     */
    'BOUTIQUE_FERMEE',
    /* ---------------------------------------------------------------------- */
    /* Paiement en ligne et facturation QR (SPEC-BOUTIQUE §4, §5)              */
    /* ---------------------------------------------------------------------- */
    /**
     * PAI-02/PAI-03 — la transition demandée n'existe pas dans le cycle de vie du
     * paiement : confirmer un paiement abandonné, rembourser un paiement jamais
     * encaissé. **Un rejeu à l'identique n'est PAS cette erreur** : il est
     * silencieusement idempotent, c'est tout l'objet de PAI-03.
     */
    'PAIEMENT_ETAT_INVALIDE',
    /**
     * Le jeton porteur présenté ne correspond pas à ce paiement (ou à cette
     * facture). 403 : c'est un refus d'autorisation, pas une donnée invalide —
     * et le message ne dit jamais si l'identifiant, lui, existe.
     */
    'JETON_INVALIDE',
    /**
     * FAC-05 — le théâtre n'a pas de configuration bancaire exploitable : pas
     * d'IBAN, ou une raison sociale/adresse incomplète. Émettre quand même
     * produirait un QR que **aucune banque n'accepterait**, découvert par le
     * client et pas par nous. On refuse, et le message dit quoi remplir.
     */
    'CONFIG_BANCAIRE_INCOMPLETE',
    /* ---------------------------------------------------------------------- */
    /* Résolution du théâtre par sous-domaine (multi-tenant, SOCLE D)          */
    /* ---------------------------------------------------------------------- */
    /*
     * Ces cinq codes voyagent jusqu'au navigateur : c'est le frontend d'un
     * théâtre qui doit afficher « adresse inconnue », « accès suspendu » ou
     * l'écran de fin d'essai. Ils appartiennent donc bien au contrat métier
     * partagé — contrairement au vocabulaire d'exploitation de la plateforme
     * (`plateforme/erreurs.ts`, réservé à la console).
     */
    /** Aucun théâtre à ce sous-domaine (404 — ne révèle rien de plus). */
    'TENANT_INCONNU',
    /** Le théâtre existe, son accès est suspendu (403 — le message l'explique). */
    'TENANT_SUSPENDU',
    /** La période d'essai est échue (403) — le front affiche la fin d'essai. */
    'TENANT_EXPIRE',
    /** Provisionnement en cours : réessayer dans un instant (503). */
    'TENANT_INDISPONIBLE',
    /**
     * ★ Fail-closed. Le contexte du théâtre n'a pas été résolu et du code a
     * quand même voulu toucher la base. 500 délibéré : mieux vaut une panne
     * bruyante que servir silencieusement les données d'un théâtre par défaut
     * (ANTICIPATION-MULTITENANT.md §6, R3).
     */
    'TENANT_ABSENT',
    /**
     * Le libre-service n'accepte pas de nouvelle demande sur cet environnement
     * (403). La route existe, elle est fermee : c'est un choix d'exploitation,
     * pas une panne, et l'ecran doit pouvoir l'expliquer plutot que de laisser
     * croire a un incident.
     */
    'INSCRIPTION_FERMEE',
];
export function estCodeErreur(valeur) {
    return typeof valeur === 'string' && CODE_ERREUR.includes(valeur);
}
/* -------------------------------------------------------------------------- */
/* Mapping code -> statut HTTP                                                */
/* -------------------------------------------------------------------------- */
/**
 * Statut HTTP par défaut de chaque code (ARCHITECTURE §5) :
 * 400 validation · 401 non authentifié · 403 rôle · 404 introuvable ·
 * **409 conflit métier (tous les codes d'invariant)** · 422 schéma.
 *
 * Deux codes portent deux statuts selon le contexte ; les constructeurs nommés
 * ci-dessous choisissent explicitement :
 *   - `NON_AUTORISE` : 401 (`AppError.nonAuthentifie`) ou 403 (`AppError.roleInsuffisant`) ;
 *   - `VALIDATION`   : 400 (`AppError.validation`) ou 422 (`AppError.schema`).
 */
export const HTTP_PAR_CODE = {
    SIEGE_OCCUPE: 409,
    TARIF_MANQUANT: 409,
    CATEGORIE_NON_VENDUE: 409,
    SOLDE_INSUFFISANT: 409,
    SPECTACLE_DEJA_CONSOMME: 409,
    QUOTA_ABONNEMENT_ATTEINT: 409,
    ZONE_INCOMPATIBLE: 409,
    SIEGE_HORS_VENTE: 409,
    SIEGE_GELE_CAMPAGNE: 409,
    SIEGE_RESERVE_SOUSCRIPTION: 409,
    SIEGE_HORS_SALLE: 409,
    REPRESENTATION_ANNULEE: 409,
    PHASE_INVALIDE: 409,
    PLAN_VERROUILLE: 409,
    VENTE_PHASE_INTERNE: 409,
    CATEGORIE_RESERVEE_GUICHET: 409,
    NON_AUTORISE: 403,
    INTROUVABLE: 404,
    VALIDATION: 400,
    TROP_DE_DEMANDES: 429,
    STOCKAGE_INDISPONIBLE: 503,
    /* Conflits métier de la boutique : l'état du monde a changé sous les pieds
       du visiteur, sa requête était valide (409 — ARCHITECTURE §5). */
    PANIER_EXPIRE: 409,
    PANIER_PLEIN: 409,
    PROLONGATION_IMPOSSIBLE: 409,
    BOUTIQUE_FERMEE: 409,
    PAIEMENT_ETAT_INVALIDE: 409,
    JETON_INVALIDE: 403,
    CONFIG_BANCAIRE_INCOMPLETE: 409,
    TENANT_INCONNU: 404,
    TENANT_SUSPENDU: 403,
    TENANT_EXPIRE: 403,
    TENANT_INDISPONIBLE: 503,
    TENANT_ABSENT: 500,
    INSCRIPTION_FERMEE: 403,
};
export function httpPourCode(code) {
    return HTTP_PAR_CODE[code];
}
/** Garde de type utilisable côté frontend sur une réponse `fetch` non-2xx. */
export function estEnveloppeErreur(valeur) {
    if (typeof valeur !== 'object' || valeur === null)
        return false;
    const erreur = valeur.erreur;
    if (typeof erreur !== 'object' || erreur === null)
        return false;
    const { code, message } = erreur;
    return estCodeErreur(code) && typeof message === 'string';
}
/* -------------------------------------------------------------------------- */
/* AppError                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Erreur métier. Les services la lèvent ; le hook d'erreur Fastify la convertit
 * en `EnveloppeErreur` avec le statut `http`.
 *
 * ⚠️ Ne jamais l'utiliser pour une erreur technique (panne SQL, timeout SMTP) :
 * celles-là remontent en 500 avec un message générique et sont journalisées.
 */
export class AppError extends Error {
    code;
    http;
    details;
    constructor(code, message, details, http) {
        super(message);
        this.name = 'AppError';
        this.code = code;
        this.http = http ?? HTTP_PAR_CODE[code];
        this.details = details;
        // Chaîne de prototype correcte après transpilation vers ES2023.
        Object.setPrototypeOf(this, AppError.prototype);
    }
    /** Corps JSON à renvoyer au client. */
    versEnveloppe() {
        return {
            erreur: this.details === undefined
                ? { code: this.code, message: this.message }
                : { code: this.code, message: this.message, details: this.details },
        };
    }
    toJSON() {
        return this.versEnveloppe();
    }
    /** Garde de type — `instanceof` reste possible, ceci survit aux realms. */
    static est(valeur) {
        return valeur instanceof AppError;
    }
    /** Reconstruit une `AppError` depuis une réponse HTTP (utilisé par `lib/api.ts`). */
    static depuisEnveloppe(valeur, http) {
        if (estEnveloppeErreur(valeur)) {
            const { code, message, details } = valeur.erreur;
            return new AppError(code, message, details, http);
        }
        return new AppError('VALIDATION', 'Réponse du serveur illisible.', undefined, http);
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — invariants                                       */
    /* ---------------------------------------------------------------------- */
    /**
     * I-01. Levée aussi bien par la pré-vérification applicative que par la
     * traduction du code PostgreSQL `23505` sur `ix_billet_siege_unique`.
     * `siege` est le libellé humain (« F12 »), pas l'uuid.
     *
     * ★ SOU-02 exige que le message désigne **le siège ET la représentation** :
     * sur une série de huit dates, « A2 est déjà occupé pour cette représentation »
     * n'apprend rien au guichet — il ne sait pas quel soir coince, ne peut ni
     * l'expliquer au client ni le corriger. `libelleRepresentation` porte donc
     * « Cyrano du 12.10.2027 20:00 » quand l'appelant sait le résoudre ; à défaut,
     * on retombe sur la formule générique plutôt que d'inventer.
     */
    static siegeOccupe(siege, representationId, details, libelleRepresentation) {
        return new AppError('SIEGE_OCCUPE', libelleRepresentation === undefined
            ? `Le siège ${siege} est déjà occupé pour cette représentation.`
            : `Le siège ${siege} est déjà occupé pour ${libelleRepresentation}.`, libelleRepresentation === undefined
            ? { siege, representationId, ...details }
            : { siege, representationId, representation: libelleRepresentation, ...details });
    }
    /**
     * ★ I-08 — le siège est **retenu par le panier d'un autre visiteur**, pas
     * vendu. La boutique publique réintroduit cette situation, que la conception
     * mono-guichet excluait (SPEC-BOUTIQUE §1.1).
     *
     * Le **code reste `SIEGE_OCCUPE`** : SPEC-BOUTIQUE §2.2 est explicite (« zéro
     * ligne renvoyée = le siège est pris par quelqu'un d'autre → SIEGE_OCCUPE »),
     * et le contrat d'erreur d'ARCHITECTURE §5 n'a donc pas à bouger — un client
     * déjà écrit continue de traiter ce refus comme un conflit de siège.
     *
     * Seul le **message** diffère, et il le doit : « déjà occupé » ferait croire
     * à une place vendue, alors qu'elle se libérera peut-être dans dix minutes.
     * Dire « retenu » et donner l'heure, c'est la différence entre « cherchez
     * ailleurs » et « réessayez tout à l'heure ».
     */
    static siegeRetenu(siege, representationId, 
    /** Heure de libération automatique, si l'appelant la connaît (REZ-02). */
    expireLe, details) {
        const jusqua = expireLe === undefined
            ? ''
            : ` jusqu’à ${new Date(expireLe).toLocaleTimeString('fr-CH', {
                timeZone: 'Europe/Zurich',
                hour: '2-digit',
                minute: '2-digit',
            })}`;
        return new AppError('SIEGE_OCCUPE', `Le siège ${siege} est retenu par un autre visiteur${jusqua} : il n’est pas disponible pour l’instant.`, {
            siege,
            representationId,
            retenu: true,
            ...(expireLe === undefined ? {} : { expireLe: new Date(expireLe).toISOString() }),
            ...details,
        });
    }
    /**
     * TAR-04. Le message nomme la case manquante de la matrice.
     *
     * ★ T-11 — `salle` distingue deux zones homonymes de salles différentes
     * (« Parterre (cat. 1) » existe côté grande salle ET côté studio) : sans
     * elle, « zone × catégorie × spectacle » n'identifie plus une case unique,
     * et le guichetier bloqué ne sait pas laquelle remplir dans la matrice.
     */
    static tarifManquant(contexte, details) {
        const zone = contexte.zone ?? 'toutes zones';
        const zoneAvecSalle = contexte.salle === undefined ? zone : `${zone} (${contexte.salle})`;
        return new AppError('TARIF_MANQUANT', `Tarif manquant : ${zoneAvecSalle} × ${contexte.categoriePublic} × ${contexte.libellePortee}. `
            + `Complétez la grille tarifaire avant de vendre.`, { ...contexte, ...details });
    }
    /**
     * ★ TAR-04 bis — la catégorie est FERMÉE sur ce spectacle, délibérément.
     *
     * Distinct de `tarifManquant`, et la distinction est tout l'intérêt : ce
     * message-là n'accuse personne d'un oubli. Un guichetier qui lit « tarif
     * manquant » va remplir la case — c'est-à-dire défaire la décision que le
     * théâtre avait prise. Il doit lire que la catégorie n'est pas vendue ici,
     * et proposer autre chose au client qui est devant lui.
     */
    static categorieNonVendue(contexte, details) {
        const zone = contexte.zone ?? 'toutes zones';
        const zoneAvecSalle = contexte.salle === undefined ? zone : `${zone} (${contexte.salle})`;
        return new AppError('CATEGORIE_NON_VENDUE', `« ${contexte.categoriePublic} » n’est pas vendu sur ${contexte.libellePortee} `
            + `(${zoneAvecSalle}) — c’est un choix du théâtre, pas un oubli. `
            + `Proposez une autre catégorie, ou rouvrez la case dans la grille tarifaire.`, { ...contexte, ...details });
    }
    /** I-03. Carnet : le solde ne peut jamais devenir négatif. */
    static soldeInsuffisant(souscriptionId, solde, demande, details) {
        return new AppError('SOLDE_INSUFFISANT', `Solde insuffisant : ${String(demande)} entrée(s) demandée(s), ${String(solde)} restante(s) sur ce carnet.`, { souscriptionId, solde, demande, ...details });
    }
    /** I-04, cas « ce spectacle est déjà consommé ». */
    static spectacleDejaConsomme(souscriptionId, spectacle, details) {
        return new AppError('SPECTACLE_DEJA_CONSOMME', `« ${spectacle} » est déjà consommé sur cet abonnement : un spectacle ne compte qu'une fois.`, { souscriptionId, spectacle, ...details });
    }
    /**
     * ★ T-32 — I-04, cas « quota de spectacles atteint ». Code DISTINCT de
     * `spectacleDejaConsomme` : ce n'est pas le même spectacle qui revient, c'est
     * le compteur de la formule qui est plein — le guichet n'a pas le même geste
     * à proposer (ici, éventuellement une dérogation d'administrateur, DEB-10).
     */
    static quotaSpectaclesAtteint(souscriptionId, nbSpectacles, details) {
        return new AppError('QUOTA_ABONNEMENT_ATTEINT', `Cet abonnement couvre ${String(nbSpectacles)} spectacle(s) : le quota est atteint.`, { souscriptionId, nbSpectacles, ...details });
    }
    /** I-05. La formule est liée à une zone ; le siège choisi est ailleurs. */
    static zoneIncompatible(siege, zoneAttendue, details) {
        return new AppError('ZONE_INCOMPATIBLE', `Le siège ${siege} n'appartient pas à la zone « ${zoneAttendue} » de cette formule.`, { siege, zoneAttendue, ...details });
    }
    /** I-06. Siège marqué hors vente (CFG-03) : aucun billet ne peut y être créé. */
    static siegeHorsVente(siege, note, details) {
        return new AppError('SIEGE_HORS_VENTE', note === undefined
            ? `Le siège ${siege} est hors vente.`
            : `Le siège ${siege} est hors vente (${note}).`, note === undefined ? { siege, ...details } : { siege, note, ...details });
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — règles de phase et de campagne                   */
    /* ---------------------------------------------------------------------- */
    /** CAM-04. Le siège est promis à un abonné sortant tant que la phase interne dure. */
    static siegeGeleCampagne(siege, details) {
        return new AppError('SIEGE_GELE_CAMPAGNE', `Le siège ${siege} est réservé par une proposition de réabonnement en attente.`, { siege, ...details });
    }
    /**
     * SOU-01. Le siège est **promis** par une souscription série encore vivante
     * (brouillon ou confirmée) : ses billets ne sont pas encore émis, donc I-01
     * ne le protège pas, mais la place a déjà été annoncée à l'abonné.
     *
     * Le message nomme le payeur : sans lui, le guichet ne peut ni expliquer au
     * client qui a la place, ni aller corriger la bonne souscription.
     */
    static siegeReserveSouscription(siege, payeur, details) {
        return new AppError('SIEGE_RESERVE_SOUSCRIPTION', `Le siège ${siege} est réservé par l'abonnement de ${payeur} : il n'est pas disponible.`, { siege, payeur, ...details });
    }
    /**
     * ★★ I-09. Le siège appartient à une AUTRE salle que celle où se joue la
     * représentation.
     *
     * Le message nomme les deux salles, et c'est le point : « le siège F12 est
     * introuvable » enverrait le guichet chercher une erreur de saisie, alors que
     * le siège existe bel et bien — ailleurs. Sur un théâtre à deux salles, les
     * libellés de fauteuils se recouvrent presque entièrement ; sans les deux
     * noms, personne ne comprend ce qui vient d'être refusé.
     *
     * C'est un conflit métier (409) et non une validation (400) : la requête est
     * bien formée, c'est l'appariement salle ↔ siège qui ne tient pas.
     */
    static siegeHorsSalle(siege, salleDuSiege, salleDeLaRepresentation, details) {
        return new AppError('SIEGE_HORS_SALLE', `Le siège ${siege} appartient à la salle « ${salleDuSiege} » ; cette représentation se joue `
            + `dans « ${salleDeLaRepresentation} ».`, { siege, salleDuSiege, salleDeLaRepresentation, ...details });
    }
    /**
     * CAT-04. La représentation est rayée du programme : aucun billet ne peut y
     * naître. C'est un **conflit métier** (409), pas une saisie fautive — le
     * corps de la requête est valide, c'est l'état du système qui a changé sous
     * les pieds du guichet (ARCHITECTURE §5).
     */
    static representationAnnulee(details) {
        return new AppError('REPRESENTATION_ANNULEE', 'Cette représentation est annulée : aucun billet ne peut y être créé.', details);
    }
    /** L'action demandée n'est pas permise dans la phase actuelle de la saison. */
    static phaseInvalide(phaseActuelle, attendu, details) {
        return new AppError('PHASE_INVALIDE', `Action impossible : la saison est en phase « ${phaseActuelle} », ${attendu}.`, { phaseActuelle, attendu, ...details });
    }
    /** CFG-04. Le plan est figé dans sa structure dès qu'un billet existe. */
    static planVerrouille(quoi, details) {
        return new AppError('PLAN_VERROUILLE', `Impossible de supprimer ${quoi} : des billets existent sur une saison non clôturée. `
            + `Le marquage « hors vente » reste possible.`, details);
    }
    /** VEN-02. Vente à l'unité en phase interne : refusée sauf dérogation admin. */
    static ventePhaseInterne(details) {
        return new AppError('VENTE_PHASE_INTERNE', `La vente à l'unité est bloquée pendant la phase interne de réabonnement. `
            + `Un administrateur peut accorder une dérogation.`, details);
    }
    /**
     * ★ T-18 (DEB-10). Un tarif `reserveGuichet` (étudiant, AVS…) suppose une
     * pièce vérifiée en main : refusé par téléphone sauf dérogation admin.
     */
    static categorieReserveeGuichet(libelleCategorie, details) {
        return new AppError('CATEGORIE_RESERVEE_GUICHET', `Le tarif « ${libelleCategorie} » suppose une pièce vérifiée en main : un administrateur ` +
            'doit déroger pour l’appliquer par téléphone.', details);
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — boutique publique (SPEC-BOUTIQUE §2.4, §3.1)     */
    /* ---------------------------------------------------------------------- */
    /**
     * ★ REZ-01/REZ-04 — le panier a expiré pendant que le visiteur l'utilisait.
     *
     * C'est **le** moment où une billetterie grand public se juge : quelqu'un a
     * passé cinq minutes à choisir ses places, deux minutes à taper son adresse,
     * et le système lui répond. « Panier introuvable » lui ferait croire à une
     * panne et lui coûterait la vente ; « expiré, les places sont reparties, en
     * voici de nouvelles » lui dit ce qui s'est passé ET ce qu'il peut faire.
     *
     * Le message assume la mauvaise nouvelle sans détour : les places ne sont
     * plus à lui. Laisser croire l'inverse produirait un second refus, plus tard,
     * et plus vexant.
     */
    static panierExpire(details) {
        return new AppError('PANIER_EXPIRE', 'Votre panier a expiré : les places retenues ont été remises en vente. '
            + 'Choisissez-les à nouveau — elles sont peut-être encore libres.', details);
    }
    /** REZ-05 — garde-fou contre l'accaparement. Le message donne la limite. */
    static panierPlein(maximum, details) {
        return new AppError('PANIER_PLEIN', `Une commande est limitée à ${String(maximum)} place(s). `
            + 'Pour un groupe plus important, contactez directement le théâtre.', { maximum, ...details });
    }
    /**
     * REZ-03 — la prolongation n'est offerte **qu'une fois**. Sans cette limite,
     * un onglet laissé ouvert immobiliserait la salle indéfiniment.
     */
    static prolongationImpossible(details) {
        return new AppError('PROLONGATION_IMPOSSIBLE', 'Votre panier a déjà été prolongé une fois. Terminez votre commande '
            + 'maintenant : les places ne peuvent pas être retenues plus longtemps.', details);
    }
    /**
     * La billetterie en ligne n'est pas ouverte. Le message dit **pourquoi** sans
     * jargon interne : un visiteur ne sait pas ce qu'est une « phase interne ».
     */
    static boutiqueFermee(raison, details) {
        return new AppError('BOUTIQUE_FERMEE', `La vente en ligne n’est pas ouverte : ${raison}`, details);
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — paiement en ligne et facturation                 */
    /* ---------------------------------------------------------------------- */
    /**
     * PAI-02 — transition impossible. Le message nomme les deux états, parce que
     * ce refus se lit aussi bien dans un journal de webhook que sur un écran.
     */
    static paiementEtatInvalide(depuis, vers, details) {
        return new AppError('PAIEMENT_ETAT_INVALIDE', `Ce paiement est « ${depuis} » : il ne peut pas devenir « ${vers} ».`, { depuis, vers, ...details });
    }
    /**
     * 403 — jeton porteur faux. Volontairement avare : même message que
     * l'identifiant existe ou non, pour qu'aucune boucle ne puisse énumérer les
     * paiements d'un théâtre.
     */
    static jetonInvalide() {
        return new AppError('JETON_INVALIDE', 'Lien de paiement invalide ou périmé.', undefined, 403);
    }
    /** FAC-05 — la configuration bancaire manque ou est incomplète. */
    static configBancaireIncomplete(manquants) {
        return new AppError('CONFIG_BANCAIRE_INCOMPLETE', `La facturation QR n’est pas configurée : ${manquants.join(', ')}. ` +
            'Complétez la configuration bancaire du théâtre.', { manquants: [...manquants] });
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — transverses                                      */
    /* ---------------------------------------------------------------------- */
    /** 401 — aucune session valide (absente, expirée, révoquée). */
    static nonAuthentifie(message = 'Session expirée, veuillez vous reconnecter.') {
        return new AppError('NON_AUTORISE', message, undefined, 401);
    }
    /** 403 — session valide, rôle insuffisant. */
    static roleInsuffisant(rolesAttendus) {
        return new AppError('NON_AUTORISE', `Vous n'avez pas les droits nécessaires pour cette action.`, { rolesAttendus: [...rolesAttendus] }, 403);
    }
    /** 403 — jeton CSRF absent ou invalide (ARCHITECTURE §4). */
    static csrfInvalide() {
        return new AppError('NON_AUTORISE', 'Requête rejetée pour raison de sécurité. Rechargez la page et réessayez.', undefined, 403);
    }
    /** 404 — `entite` est le nom métier (« Souscription », « Représentation »). */
    static introuvable(entite, id) {
        return new AppError('INTROUVABLE', `${entite} introuvable.`, id === undefined ? { entite } : { entite, id }, 404);
    }
    /** 400 — règle de saisie métier non respectée (motif obligatoire, montant négatif…). */
    static validation(message, details) {
        return new AppError('VALIDATION', message, details, 400);
    }
    /** 422 — le corps ne respecte pas le schéma Zod. `details.champs` liste les erreurs. */
    static schema(details) {
        return new AppError('VALIDATION', 'Les données envoyées sont incomplètes ou mal formées.', details, 422);
    }
    /**
     * 429 — limitation de débit (A-02 : login, lien magique, inscription).
     * ★ T-32 — portait le code `VALIDATION` depuis l'origine ; un débit dépassé
     * n'est pas une donnée invalide.
     */
    static tropDeDemandes(message = 'Trop de demandes. Réessayez dans une heure.') {
        return new AppError('TROP_DE_DEMANDES', message, undefined, 429);
    }
    /**
     * ★★ LOT A — 503, le stockage des fichiers est momentanément indisponible.
     *
     * Le message dit deux choses, et les deux comptent : que la panne est
     * transitoire (donc qu'il faut réessayer, pas appeler au secours), et que le
     * RESTE du théâtre fonctionne. C'est vrai par construction — aucune vente,
     * aucun scan, aucune connexion ne touche le stockage d'objets — et c'est
     * exactement ce qu'un exploitant a besoin d'entendre avant de décider s'il
     * annule sa soirée.
     */
    static stockageIndisponible(message = 'Le stockage des fichiers est momentanément indisponible. Le reste du théâtre fonctionne normalement ; réessayez dans un instant.') {
        return new AppError('STOCKAGE_INDISPONIBLE', message, undefined, 503);
    }
    /* ---------------------------------------------------------------------- */
    /* Constructeurs nommés — résolution du théâtre (SOCLE D, multi-tenant)    */
    /* ---------------------------------------------------------------------- */
    /**
     * 404 — le sous-domaine ne désigne aucun théâtre en service.
     *
     * Même réponse pour « ce slug n'existe pas » et « ce théâtre est archivé » :
     * distinguer les deux transformerait la page d'accueil en oracle
     * d'énumération des clients de la plateforme. Le détail ne contient que
     * l'hôte demandé, que l'appelant connaît déjà.
     */
    static tenantInconnu(hote) {
        return new AppError('TENANT_INCONNU', 'Aucun théâtre n’est desservi à cette adresse.', { hote }, 404);
    }
    /** 403 — le théâtre existe mais son accès est suspendu. Le motif est dit. */
    static tenantSuspendu(nom, motif) {
        return new AppError('TENANT_SUSPENDU', motif === undefined || motif === null || motif === ''
            ? `L’accès à ${nom} est suspendu. Contactez votre interlocuteur habituel.`
            : `L’accès à ${nom} est suspendu : ${motif}`, motif === undefined || motif === null || motif === '' ? { nom } : { nom, motif }, 403);
    }
    /**
     * 403 — la période d'essai est échue. Code distinct de `TENANT_SUSPENDU`
     * parce que l'écran est distinct : une fin d'essai se prolonge, une
     * suspension se discute.
     */
    static tenantExpire(nom, expireLe) {
        const quand = expireLe === undefined || expireLe === null
            ? undefined
            : expireLe instanceof Date
                ? expireLe.toISOString()
                : expireLe;
        return new AppError('TENANT_EXPIRE', `La période d’essai de ${nom} est terminée. Les données sont conservées le temps de la prolonger.`, quand === undefined ? { nom } : { nom, expireLe: quand }, 403);
    }
    /** 503 — la base du théâtre est en cours de préparation. */
    static tenantIndisponible(nom) {
        return new AppError('TENANT_INDISPONIBLE', `${nom} est en cours de préparation. Réessayez dans un instant.`, { nom }, 503);
    }
    /**
     * ★ 500 — fail-closed : du code a demandé la base sans contexte de théâtre.
     *
     * C'est un défaut de programmation, pas une saisie fautive : une route a
     * échappé à la résolution du sous-domaine. On préfère une panne bruyante et
     * traçable à un repli silencieux sur un théâtre par défaut, qui servirait
     * les données du mauvais client (ANTICIPATION-MULTITENANT.md §6, R3).
     */
    static tenantAbsent(ou = 'accès à la base') {
        return new AppError('TENANT_ABSENT', 'Contexte de théâtre absent : la requête a été refusée par sécurité.', { ou }, 500);
    }
}
//# sourceMappingURL=erreurs.js.map